import {
  useEffect, useRef, useState, ReactNode, DragEvent, ChangeEvent,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface ProcessedFile {
  name: string;
  extension: string;
  path: string | ArrayBuffer | null;
}

interface PaperLikeProps {
  variant?: 'outlined' | 'elevation';
  elevation?: number;
}

interface FileUploadProps {
  title?: string;
  multiFile?: boolean;
  acceptedType?: string;
  allowedExtensions?: string[];
  maxFileSize?: number;
  maxUploadFiles?: number;
  // Return type allows Promise so callers may pass an async handler; the
  // component invokes it fire-and-forget (it never awaits the result).
  onFilesChange?: (files: ProcessedFile[]) => void | Promise<void>;
  onError?: (message: string) => void;
  bannerProps?: PaperLikeProps;
  containerProps?: PaperLikeProps;
  buttonLabel?: ReactNode;
  buttonRemoveLabel?: ReactNode;
  maxFilesContainerHeight?: number;
  disabled?: boolean;
}

function readAsDataURL(file: File): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getExtension(filename: string) {
  return (filename.split('.').pop() || '').toLowerCase();
}

export default function FileUpload({
  title = '',
  multiFile = false,
  acceptedType = '*',
  allowedExtensions = [],
  maxFileSize = 10,
  maxUploadFiles = 1,
  onFilesChange,
  onError,
  bannerProps = {},
  containerProps = {},
  buttonLabel = 'click here',
  buttonRemoveLabel = 'Remove all',
  maxFilesContainerHeight,
  disabled = false,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onFilesChangeRef = useRef(onFilesChange);
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const previousFilesRef = useRef<ProcessedFile[]>(files);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    onFilesChangeRef.current = onFilesChange;
  }, [onFilesChange]);

  useEffect(() => {
    if (previousFilesRef.current === files) {
      return;
    }
    previousFilesRef.current = files;
    void onFilesChangeRef.current?.(files);
  }, [files]);

  async function processFiles(raw: File[]) {
    const results: ProcessedFile[] = [];
    for (const f of raw) {
      const ext = getExtension(f.name);
      if (allowedExtensions.length && !allowedExtensions.includes(ext)) {
        onError?.(`File type .${ext} is not allowed`);
        continue;
      }
      if (f.size > maxFileSize * 1024 * 1024) {
        onError?.(`${f.name} exceeds the ${maxFileSize}MB size limit`);
        continue;
      }
      const path = await readAsDataURL(f);
      results.push({ name: f.name, extension: ext, path });
    }
    return results;
  }

  async function handleFiles(raw: FileList | File[]) {
    const list = Array.from(raw);
    const selected = multiFile ? list : list.slice(0, 1);
    const capped = maxUploadFiles > 0 ? selected.slice(0, maxUploadFiles) : selected;
    const processed = await processFiles(capped);
    if (!processed.length) return;
    setFiles((prev) => {
      const next = multiFile ? [...prev, ...processed] : processed;
      return maxUploadFiles > 0 ? next.slice(0, maxUploadFiles) : next;
    });
  }

  function removeFile(idx: number) {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
  }

  function removeAll() {
    setFiles([]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    void handleFiles(e.dataTransfer.files);
  }

  return (
    <Paper
      variant={containerProps.variant ?? 'outlined'}
      elevation={containerProps.elevation ?? 0}
      sx={{ p: 2 }}
    >
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      <Paper
        variant={bannerProps.variant ?? 'outlined'}
        elevation={bannerProps.elevation ?? 0}
        sx={{
          p: 2,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          border: dragging ? '2px dashed' : undefined,
          borderColor: dragging ? 'primary.main' : undefined,
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <Typography variant="body2" sx={{
          color: 'text.secondary',
        }}>
          [Drag to drop] or{' '}
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); if (!disabled) inputRef.current?.click(); }}
            sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', fontWeight: 500, fontSize: 'inherit' }}
          >
            {buttonLabel}
          </Button>
          {' '}to select {multiFile ? 'files' : 'file'}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={acceptedType}
          multiple={multiFile}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            void handleFiles(e.target.files || []);
            e.target.value = '';
          }}
        />
      </Paper>
      {files.length > 0 && (
        <Box sx={{ maxHeight: maxFilesContainerHeight ?? 357, overflow: 'auto' }}>
          <List dense>
            {files.map((f, idx) => (
              <ListItem
                key={idx}
                secondaryAction={(
                  <IconButton edge="end" size="small" onClick={() => removeFile(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <ListItemText primary={f.name} />
              </ListItem>
            ))}
          </List>
          <Button size="small" color="error" onClick={removeAll}>
            {buttonRemoveLabel}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
